import { mode, orient } from '@chakra-ui/theme-tools';

import { type StyleFunctionNonMulti } from '@frontend/legacy-components/src/theme/styleConfigHelpers';

import { SLIDER_PARTS } from './sliderParts';
import type {
  SliderColorScheme,
  SliderStyleConfig,
  SliderVariant,
} from './types';

const thumbOrientation: StyleFunctionNonMulti<
  SliderVariant,
  SliderColorScheme
> = ({ orientation }) =>
  orient({
    orientation,
    vertical: {
      left: '50%',
      transform: `translateX(-50%)`,
      _active: {
        transform: `translateX(-50%) scale(1.15)`,
      },
    },
    horizontal: {
      top: '50%',
      transform: `translateY(-50%)`,
      _active: {
        transform: `translateY(-50%) scale(1.15)`,
      },
    },
  });

const sizes: SliderStyleConfig['sizes'] = {
  lg: ({ orientation }) => {
    return {
      thumb: { w: '16px', h: '16px' },
      track: orient({
        orientation,
        horizontal: { h: '4px' },
        vertical: { w: '4px' },
      }),
    };
  },
  md: ({ orientation }) => {
    return {
      thumb: { w: '14px', h: '14px' },
      track: orient({
        orientation,
        horizontal: { h: '4px' },
        vertical: { w: '4px' },
      }),
    };
  },
  sm: ({ orientation }) => {
    return {
      thumb: { w: '10px', h: '10px' },
      track: orient({
        orientation,
        horizontal: { h: '2px' },
        vertical: { w: '2px' },
      }),
    };
  },
};

const baseStyle: SliderStyleConfig['baseStyle'] = (styleFunctionProps) => ({
  container: {
    display: 'inline-block',
    position: 'relative',
    cursor: 'pointer',
    _disabled: {
      opacity: 0.6,
      cursor: 'default',
      pointerEvents: 'none',
    },
    ...orient({
      orientation: styleFunctionProps.orientation,
      vertical: { h: '100%' },
      horizontal: { w: '100%' },
    }),
  },
  track: {
    overflow: 'hidden',
    borderRadius: 'sm',
    bg: mode(
      'interaction.disabled',
      'interaction.disabled',
    )(styleFunctionProps),
    _disabled: {
      bg: mode(
        'interaction.disabled',
        'interaction.disabled',
      )(styleFunctionProps),
    },
  },
  thumb: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    outline: 0,
    zIndex: 1,
    borderRadius: 'full',
    bg: 'white',
    boxShadow: 'base',
    border: '3px solid',
    borderColor: 'interaction.main',
    transitionProperty: 'transform',
    transitionDuration: 'normal',
    _focus: { boxShadow: 'outline' },
    _disabled: { bg: 'interaction.disabled' },
    ...thumbOrientation(styleFunctionProps),
  },
  filledTrack: {
    width: 'inherit',
    height: 'inherit',
    bg: 'interaction.main',
  },
});

const defaultProps = {
  size: 'md',
  colorScheme: 'blue',
} as const;

export const SLIDER_STYLE_CONFIG: SliderStyleConfig = {
  parts: SLIDER_PARTS,
  sizes,
  baseStyle,
  defaultProps,
};
